/**
 * @fileoverview Inspector-selection value helpers for primary inspector override.
 * @module inspection-renderer/inspectors
 *
 * Responsibilities:
 * - Define the renderer-local value shape emitted by the `inspectors` widget.
 * - Normalize inspector rosters from host/event data, field configuration, and
 *   persisted field responses.
 * - Extract the selected primary inspector from persisted responses so conduct
 *   mode can attribute violations, snapshots, and completion to that person
 *   instead of blindly using the logged-in user.
 *
 * Dependencies:
 * - None.
 *
 * Architecture:
 * - Pure value helpers. No React, no Dexie, no host imports.
 *
 * @localFirst
 *
 * Source of Truth:
 * - local
 *
 * Write Flow:
 * - Inspector choices are emitted as field response values and persisted by the
 *   host's local-first draft/session adapter.
 *
 * Sync Strategy:
 * - Responses sync through the existing inspection response synchronization
 *   path; these helpers do not perform storage or network access.
 *
 * Conflict Resolution:
 * - Host response conflict handling applies. Helpers choose the first valid
 *   primary selection found in the supplied response map.
 *
 * Notes:
 * - Hosts may pass event participants through `inspectorRoster`; templates may
 *   also provide static options in `field.extras.inspectorOptions`.
 */

/**
 * @contract InspectorRosterEntry
 *
 * Purpose:
 * - One selectable inspector in an inspection roster.
 *
 * Fields:
 * - id: stable user/inspector id.
 * - name: display name.
 * - role: optional role label.
 * - status: optional online/offline/locked presence state.
 * - isPrimary: optional visual/source flag.
 *
 * Rules:
 * - `id` must be non-empty after trimming.
 *
 * Sync:
 * - Stored inside a normal field response when emitted by the widget.
 */
export interface InspectorRosterEntry {
  id: string;
  name: string;
  role?: string;
  status?: 'online' | 'offline' | 'locked';
  isPrimary?: boolean;
}

/**
 * @contract InspectorSelectionValue
 *
 * Purpose:
 * - Persisted value shape for the `inspectors` widget.
 *
 * Fields:
 * - valueKind: discriminator for safe response scanning.
 * - inspectors: roster visible at selection time.
 * - primaryInspectorId: selected primary inspector id.
 * - primaryInspectorName: selected primary inspector display name.
 * - updatedAt: ISO timestamp for the last primary change.
 *
 * Rules:
 * - Exactly one `primaryInspectorId` should be selected when the field is
 *   required or when it is intended to override attribution.
 *
 * Sync:
 * - Local-first field response value; host adapters persist it like any other
 *   inspection answer.
 */
export interface InspectorSelectionValue {
  valueKind: 'inspectorSelection';
  inspectors: InspectorRosterEntry[];
  primaryInspectorId?: string;
  primaryInspectorName?: string;
  updatedAt?: string;
}

/**
 * @contract PrimaryInspectorSelection
 *
 * Purpose:
 * - Minimal selected-primary inspector override consumed by renderer and mobile
 *   completion flows.
 *
 * Fields:
 * - id: selected inspector id.
 * - name: optional display name used when stamping completion and violation
 *   attribution metadata.
 *
 * Rules:
 * - `id` must be non-empty.
 *
 * Sync:
 * - Derived from local-first field responses; not persisted separately.
 */
export interface PrimaryInspectorSelection {
  id: string;
  name?: string;
}

type ResponseLike = { value?: unknown };

/**
 * @process NormalizeInspectorRosterEntry
 *
 * Steps:
 * 1. Accept host/template/persisted roster-like input.
 * 2. Read known id/name/role aliases.
 * 3. Return a canonical renderer roster entry or null when no id exists.
 *
 * Inputs:
 * - input: unknown roster-like value.
 *
 * Outputs:
 * - Canonical `InspectorRosterEntry` or null.
 *
 * Failure Handling:
 * - Invalid values are ignored by returning null.
 *
 * Idempotency:
 * - REQUIRED.
 */
export function normalizeInspectorRosterEntry(input: unknown): InspectorRosterEntry | null {
  if (!input || typeof input !== 'object') return null;
  const obj = input as Record<string, unknown>;
  const idValue = obj.id ?? obj.userId ?? obj.inspectorId ?? obj.value ?? obj._id;
  const id = typeof idValue === 'string' ? idValue.trim() : '';
  if (!id) return null;
  const nameValue = obj.name ?? obj.displayName ?? obj.inspectorName ?? obj.label ?? obj.fullName;
  const name = typeof nameValue === 'string' && nameValue.trim() ? nameValue.trim() : id;
  const roleValue = obj.role ?? obj.title;
  const status = obj.status === 'online' || obj.status === 'offline' || obj.status === 'locked'
    ? obj.status
    : undefined;
  return {
    id,
    name,
    role: typeof roleValue === 'string' && roleValue.trim() ? roleValue.trim() : undefined,
    status,
    isPrimary: obj.isPrimary === true,
  };
}

/**
 * @process MergeInspectorRosters
 *
 * Steps:
 * 1. Iterate roster sources in priority order.
 * 2. Normalize each entry.
 * 3. Keep the first entry for each id and discard duplicates.
 *
 * Inputs:
 * - sources: optional roster arrays from persisted value, host context, and field
 *   configuration.
 *
 * Outputs:
 * - Deduplicated canonical roster.
 *
 * Failure Handling:
 * - Invalid entries are skipped.
 *
 * Idempotency:
 * - REQUIRED.
 */
export function mergeInspectorRosters(...sources: Array<readonly unknown[] | undefined>): InspectorRosterEntry[] {
  const out: InspectorRosterEntry[] = [];
  const seen = new Set<string>();
  for (const source of sources) {
    for (const raw of source ?? []) {
      const entry = normalizeInspectorRosterEntry(raw);
      if (!entry || seen.has(entry.id)) continue;
      seen.add(entry.id);
      out.push(entry);
    }
  }
  return out;
}

/**
 * @process NormalizeInspectorSelectionValue
 *
 * Steps:
 * 1. Validate that the response value is object-like.
 * 2. Normalize persisted roster entries.
 * 3. Resolve primary id/name aliases into the canonical selection shape.
 *
 * Inputs:
 * - value: unknown field response value.
 *
 * Outputs:
 * - Canonical `InspectorSelectionValue` or null.
 *
 * Failure Handling:
 * - Values without a primary id and roster are ignored by returning null.
 * - Values with a roster but no primary id are retained as valid unanswered
 *   intermediate state.
 *
 * Idempotency:
 * - REQUIRED.
 */
export function normalizeInspectorSelectionValue(value: unknown): InspectorSelectionValue | null {
  if (!value || typeof value !== 'object') return null;
  const obj = value as Record<string, unknown>;
  const primaryValue = obj.primaryInspectorId ?? obj.primaryId ?? obj.inspectorId;
  const primaryInspectorId = typeof primaryValue === 'string' ? primaryValue.trim() : '';
  const inspectors = mergeInspectorRosters(Array.isArray(obj.inspectors) ? obj.inspectors : undefined);
  if (!primaryInspectorId && inspectors.length === 0) return null;
  const matchedPrimary = inspectors.find((entry) => entry.id === primaryInspectorId);
  const nameValue = obj.primaryInspectorName ?? obj.primaryName ?? obj.inspectorName;
  const primaryInspectorName = typeof nameValue === 'string' && nameValue.trim()
    ? nameValue.trim()
    : matchedPrimary?.name;
  return {
    valueKind: 'inspectorSelection',
    inspectors,
    primaryInspectorId: primaryInspectorId || undefined,
    primaryInspectorName,
    updatedAt: typeof obj.updatedAt === 'string' ? obj.updatedAt : undefined,
  };
}

/**
 * @process GetPrimaryInspectorFromValue
 *
 * Steps:
 * 1. Normalize a possible inspector-selection response value.
 * 2. Return the selected primary id/name when present.
 *
 * Inputs:
 * - value: unknown field response value.
 *
 * Outputs:
 * - Minimal primary inspector override or null.
 *
 * Failure Handling:
 * - Invalid or incomplete selections return null.
 *
 * Idempotency:
 * - REQUIRED.
 */
export function getPrimaryInspectorFromValue(value: unknown): PrimaryInspectorSelection | null {
  const selection = normalizeInspectorSelectionValue(value);
  if (!selection?.primaryInspectorId) return null;
  return {
    id: selection.primaryInspectorId,
    name: selection.primaryInspectorName,
  };
}

/**
 * @process GetPrimaryInspectorFromResponses
 *
 * Steps:
 * 1. Iterate persisted response records.
 * 2. Extract each raw response value.
 * 3. Return the first valid inspector-selection primary override.
 *
 * Inputs:
 * - responses: map of field ids to response records or raw values.
 *
 * Outputs:
 * - Minimal primary inspector override or null.
 *
 * Failure Handling:
 * - Missing or invalid response maps return null.
 *
 * Idempotency:
 * - REQUIRED.
 */
export function getPrimaryInspectorFromResponses(
  responses: Record<string, ResponseLike | unknown> | undefined,
): PrimaryInspectorSelection | null {
  for (const response of Object.values(responses ?? {})) {
    const rawValue = response && typeof response === 'object' && 'value' in response
      ? (response as ResponseLike).value
      : response;
    const primary = getPrimaryInspectorFromValue(rawValue);
    if (primary) return primary;
  }
  return null;
}
